import ReactPaginate from 'react-paginate';
import styled from 'styled-components';
import type { HistoryFetchStatus } from '../../types/exportTypes';

interface ExportPaginationSectionProps {
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  fetchReportHistory: (page: number, pageSize: number) => void;
  pageSize: number;
  historyFetchStatus: HistoryFetchStatus;
}

const ExportPaginationSection = ({
  totalPages,
  currentPage,
  setCurrentPage,
  fetchReportHistory,
  pageSize,
  historyFetchStatus
}: ExportPaginationSectionProps) => {
  // Don't render pagination if there's only one page or no pages
  if (totalPages <= 1) {
    return null;
  }

  const handlePageChange = (selectedPage: { selected: number }) => {
    const newPage = selectedPage.selected + 1; // ReactPaginate uses 0-based indexing
    setCurrentPage(newPage);
    fetchReportHistory(newPage, pageSize);
  };

  return (
    <Wrapper>
      <ReactPaginate
        pageCount={totalPages}
        forcePage={currentPage - 1} // Convert to 0-based for ReactPaginate
        marginPagesDisplayed={2}
        pageRangeDisplayed={3}
        breakLabel={'...'}
        onPageChange={handlePageChange}
        containerClassName={'pagination my-3 justify-content-center'}
        pageClassName={'page-item'}
        pageLinkClassName={'page-link'}
        previousClassName={'page-item'}
        previousLinkClassName={'page-link'}
        nextClassName={'page-item'}
        nextLinkClassName={'page-link'}
        breakClassName={'page-item'}
        breakLinkClassName={'page-link'}
        activeClassName={'active'}
        disabledClassName={'disabled'}
        previousLabel={'Previous'}
        nextLabel={'Next'}
        disableInitialCallback={true}
      />

      {/* Records info */}
      {historyFetchStatus.data && (
        <div className="pagination-info">
          Showing {(currentPage - 1) * pageSize + 1} to{' '}
          {Math.min(currentPage * pageSize, historyFetchStatus.data.total_records)} of{' '}
          {historyFetchStatus.data.total_records} exports
        </div>
      )}
    </Wrapper>
  );
};

export default ExportPaginationSection;

const Wrapper = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 0;
  border-top: 1px solid ${({ theme }) => theme.borderLight || '#e9ecef'};

  .pagination {
    font-size: 14px;
    margin-bottom: 0;

    .page-item {
      margin: 0 2px;

      &:first-child .page-link {
        border-top-left-radius: 0.375rem;
        border-bottom-left-radius: 0.375rem;
      }

      &:last-child .page-link {
        border-top-right-radius: 0.375rem;
        border-bottom-right-radius: 0.375rem;
      }

      &.active .page-link {
        background-color: ${({ theme }) => theme.primary || '#0d6efd'};
        border-color: ${({ theme }) => theme.primary || '#0d6efd'};
        color: white;
        z-index: 3;
      }

      &.disabled .page-link {
        color: ${({ theme }) => theme.textMuted || '#6c757d'};
        pointer-events: none;
        background-color: ${({ theme }) => theme.backgroundLight || '#fff'};
        border-color: ${({ theme }) => theme.borderLight || '#dee2e6'};
      }

      .page-link {
        position: relative;
        display: block;
        padding: 0.5rem 0.75rem;
        margin-left: -1px;
        line-height: 1.25;
        color: ${({ theme }) => theme.primary || '#0d6efd'};
        text-decoration: none;
        background-color: ${({ theme }) => theme.backgroundLight || '#fff'};
        border: 1px solid ${({ theme }) => theme.borderLight || '#dee2e6'};
        transition:
          color 0.15s ease-in-out,
          background-color 0.15s ease-in-out,
          border-color 0.15s ease-in-out,
          box-shadow 0.15s ease-in-out;

        &:hover {
          z-index: 2;
          color: ${({ theme }) => theme.primaryHover || '#0a58ca'};
          background-color: ${({ theme }) => theme.backgroundHover || '#e9ecef'};
          border-color: ${({ theme }) => theme.borderHover || '#dee2e6'};
        }

        &:focus {
          z-index: 3;
          outline: 0;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }
      }
    }
  }

  .pagination-info {
    color: ${({ theme }) => theme.textMuted || '#6c757d'};
    font-size: 14px;
    text-align: center;
  }

  @media (max-width: 768px) {
    .pagination {
      font-size: 12px;

      .page-item .page-link {
        padding: 0.375rem 0.5rem;
      }
    }

    .pagination-info {
      font-size: 12px;
    }
  }
`;
